"use client";

import { useMemo, useCallback } from "react";
import { isDateBlocked, areAllTimeslotsBlocked } from "@/lib/utils/rental-blocks";
import type {
  ProductDetail,
  ProductRentalBlock,
  BranchOperationBlock,
} from "@/types";

import { minimumBookingDate } from "@/lib/utils/booking-lead-time";

const MAX_RENTAL_DAYS = 90;

interface UseBookingDateBlockingParams {
  product: ProductDetail;
  productBlocks: ProductRentalBlock[];
  branchBlocks: BranchOperationBlock[];
  selectedPickupBranch: string;
  selectedReturnBranch: string;
  pickupDate: Date | undefined;
  returnDate: Date | undefined;
  pickupTime: string;
  timeOptions: string[];
}

export function useBookingDateBlocking({
  product,
  productBlocks,
  branchBlocks,
  selectedPickupBranch,
  selectedReturnBranch,
  pickupDate,
  returnDate,
  pickupTime,
  timeOptions,
}: UseBookingDateBlockingParams) {
  const isDelivery = (branchId: string) => branchId === "delivery";
  const isShipping = (branchId: string) => branchId === "shipping";

  const hasDeliveryMethod = product.usage_methods?.some(
    (method) => method.method_type === "delivery" && method.enabled,
  );

  const hasShippingMethod = product.usage_methods?.some(
    (method) => method.method_type === "shipping" && method.enabled,
  );

  const hasPickupMethod = product.usage_methods?.some(
    (method) => method.method_type === "pickup" && method.enabled,
  );

  const hasPickupOptions =
    (hasPickupMethod &&
      product.branches?.some(
        (b) => b.location_type === "pickup" || b.location_type === "both",
      )) ||
    hasShippingMethod ||
    hasDeliveryMethod;

  const hasReturnOptions =
    (hasPickupMethod &&
      product.branches?.some(
        (b) => b.location_type === "return" || b.location_type === "both",
      )) ||
    hasShippingMethod ||
    hasDeliveryMethod;

  const dateSelectionDisabled = hasPickupOptions && !selectedPickupBranch;

  const isDateBlockedForBranch = (date: Date, branchId: string) => {
    if (!branchId) return false;
    return isDateBlocked(date, product.id, branchId, productBlocks, branchBlocks);
  };

  // Timeslot products: a date is only fully unavailable when EVERY timeslot is
  // blocked. Partial slot blocks keep the date selectable (slots disabled in the
  // booking card). allSlotOrders = the product's distinct timeslot display_orders.
  const isTimeslotProduct = product.rental_unit === "timeslot";
  const allSlotOrders = useMemo(() => {
    if (!isTimeslotProduct) return [] as number[];
    const orders = new Set<number>();
    (product.pricing ?? []).forEach((p) => {
      if (p.pricing_type === "timeslot") orders.add(p.display_order ?? 0);
    });
    return [...orders];
  }, [product.pricing, isTimeslotProduct]);

  // Lead days per method type
  const pickupLeadDays = useMemo(() => {
    const method = product.usage_methods?.find(
      (m) => m.method_type === "pickup" && m.enabled,
    );
    return method?.delivery_lead_days ?? 0;
  }, [product.usage_methods]);

  const deliveryLeadDays = useMemo(() => {
    const method = product.usage_methods?.find(
      (m) => m.method_type === "delivery" && m.enabled,
    );
    return method?.delivery_lead_days ?? 0;
  }, [product.usage_methods]);

  const shippingLeadDays = useMemo(() => {
    const method = product.usage_methods?.find(
      (m) => m.method_type === "shipping" && m.enabled,
    );
    return method?.delivery_lead_days ?? 0;
  }, [product.usage_methods]);

  const getLeadDaysMinDate = useCallback(
    (leadDays: number) => {
      const date = minimumBookingDate(leadDays, product.partner_timezone || "Asia/Seoul");
      const [year, month, day] = date.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    [product.partner_timezone],
  );

  // Single disabledDate callback — replaces 4 inline copies
  const getDisabledDate = useCallback(() => {
    return (date: Date) => {
      const today = new Date(new Date().setHours(0, 0, 0, 0));
      if (date < today) return true;

      if (selectedPickupBranch) {
        const leadDays = isDelivery(selectedPickupBranch)
          ? deliveryLeadDays
          : isShipping(selectedPickupBranch)
            ? shippingLeadDays
            : pickupLeadDays;
        if (leadDays > 0) {
          const minDate = getLeadDaysMinDate(leadDays);
          if (date < minDate) return true;
        }
      }

      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + MAX_RENTAL_DAYS);
      if (date > maxDate) return true;

      // 택배 제한(method-scoped block)은 고객이 그 수령 방법을 골랐을 때만 적용된다.
      // "delivery"/"shipping" 유사 지점 id 를 그대로 넘겨야 productBlockAppliesTo 가
      // 매칭한다. 실제 지점 uuid 와 겹치지 않으므로 지점-스코프 블록/지점 운영
      // 블록은 예전처럼 매칭되지 않는다 (= 기존 "" 동작과 동일).
      const branchForBlocks = selectedPickupBranch || "";

      if (isTimeslotProduct) {
        // Disabled only if every slot is unavailable (whole-date block or all
        // slots blocked). Partial slot blocks stay selectable.
        return areAllTimeslotsBlocked(
          date,
          product.id,
          branchForBlocks,
          productBlocks,
          branchBlocks,
          allSlotOrders,
        );
      }

      // 상품 전역 블록 + (실지점이면) 지점 블록 + (택배/숙소면) 방법별 블록.
      // isDateBlocked 가 세 종류를 모두 훑으므로 한 번 호출로 충분하다.
      return isDateBlocked(
        date,
        product.id,
        branchForBlocks,
        productBlocks,
        branchBlocks,
      );
    };
  }, [
    selectedPickupBranch,
    pickupLeadDays,
    deliveryLeadDays,
    shippingLeadDays,
    getLeadDaysMinDate,
    productBlocks,
    branchBlocks,
    product.id,
    isTimeslotProduct,
    allSlotOrders,
  ]);

  // Available time options filtered by branch operating hours
  const getAvailableTimeOptions = (
    branchId: string,
    date: Date | undefined,
  ) => {
    if (!branchId || !date) return timeOptions;

    const branch = product.branches.find((b) => b.id === branchId);
    if (!branch?.operating_hours?.length) return timeOptions;

    const dayOfWeek = date.getDay();
    const dayHours = branch.operating_hours.find(
      (oh) => oh.day_of_week === dayOfWeek && !oh.is_closed,
    );

    if (!dayHours?.open_time || !dayHours?.close_time) return [];

    const openTime = dayHours.open_time.substring(0, 5);
    const closeTime = dayHours.close_time.substring(0, 5);

    return timeOptions.filter((time) => time >= openTime && time <= closeTime);
  };

  const availablePickupTimeOptions = useMemo(() => {
    return getAvailableTimeOptions(selectedPickupBranch, pickupDate);
  }, [selectedPickupBranch, pickupDate, timeOptions, product.branches]);

  const availableReturnTimeOptions = useMemo(() => {
    const baseOptions = getAvailableTimeOptions(selectedReturnBranch, returnDate);

    if (
      pickupDate &&
      returnDate &&
      pickupDate.toDateString() === returnDate.toDateString() &&
      pickupTime
    ) {
      return baseOptions.filter((time) => time > pickupTime);
    }

    return baseOptions;
  }, [selectedReturnBranch, returnDate, timeOptions, product.branches, pickupDate, pickupTime]);

  return {
    getDisabledDate,
    availablePickupTimeOptions,
    availableReturnTimeOptions,
    hasPickupOptions,
    hasReturnOptions,
    dateSelectionDisabled,
    isDelivery,
    isShipping,
    isDateBlockedForBranch,
    hasDeliveryMethod,
    hasShippingMethod,
    hasPickupMethod,
  };
}
